import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DeliveriesDeliveryRedeliverRequest,
  DeliveriesDeliveryRetryRequest,
  DeliveriesDeliverySendRequest,
  ExportsExportCreatedResponse,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { markDeliveriesPending, type PendingField } from './cache';
import { buildDeliveriesExportBody, type DeliveryExportInput } from './utils';

/**
 * Delivery bulk-op + export mutations.
 *
 * send/retry/redeliver POST the selected `delivery_ids`; on success they flip
 * the optimistic `pending*` flag on the affected rows (gated by
 * `canBeSentOrDeleted`) rather than refetching — matching the Ember controller.
 *
 * NOTE on delete: the Ember `DELETE deliveries/destroy` bulk-body endpoint has
 * no typed contract in `hydra-ui-api` (`DELETE /deliveries` declares
 * `requestBody?: never`). This implements the selected-rows case as a type-safe
 * per-id loop over `DELETE /deliveries/{id}`. Filter-based bulk delete (delete
 * all rows matching a campaign/filter with no explicit ids) is NOT supported
 * here and needs a Services OpenAPI change — see the PR description.
 */

interface BulkOpBase {
  workspaceId: number;
  deliveryIds: string[];
}

interface BulkOpHookOptions<TVariables> {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, TVariables>;
}

/**
 * Shared wrapper for the optimistic bulk-op hooks. Resolves the workspace id,
 * runs `perform`, and on success marks the affected rows with `pendingField`
 * (gated in `cache.ts`) before invoking the caller's `onSuccess`.
 */
function useBulkDeliveryMutation<TVariables extends { deliveryIds: string[] }>({
  options,
  perform,
  pendingField,
}: {
  options: BulkOpHookOptions<TVariables>;
  perform: (workspaceId: number, variables: TVariables) => Promise<void>;
  pendingField: PendingField;
}) {
  const workspaceId = options.workspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = options.mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: TVariables) => perform(workspaceId, variables),
    onSuccess: (result, variables, ...callbackArgs) => {
      markDeliveriesPending(qc, workspaceId, variables.deliveryIds, pendingField);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
}

/* --- send --- */
export interface SendDeliveriesInput extends BulkOpBase {
  type?: string;
}

/** @public */
export async function sendDeliveries(input: SendDeliveriesInput): Promise<void> {
  await hydraUiApi.deliveries.send({
    params: { path: { environment_id: String(input.workspaceId) } },
    // Generated request types every field as required; we send only the
    // selected-rows subset the backend needs (Ember does the same).
    body:
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      {
        delivery_ids: input.deliveryIds,
        type: input.type ?? 'all',
      } as DeliveriesDeliverySendRequest,
  });
}

type SendVariables = { deliveryIds: string[]; type?: string };

/** @public */
export const useSendDeliveries = (options: BulkOpHookOptions<SendVariables> = {}) =>
  useBulkDeliveryMutation<SendVariables>({
    options,
    perform: (workspaceId, variables) => sendDeliveries({ workspaceId, ...variables }),
    pendingField: 'pendingSend',
  });

/* --- retry (adds `metric`) --- */
export interface RetryDeliveriesInput extends BulkOpBase {
  type?: string;
  metric?: string;
}

/** @public */
export async function retryDeliveries(input: RetryDeliveriesInput): Promise<void> {
  await hydraUiApi.deliveries.retry({
    params: { path: { environment_id: String(input.workspaceId) } },
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    body: {
      delivery_ids: input.deliveryIds,
      type: input.type ?? 'all',
      metric: input.metric ?? '',
    } as DeliveriesDeliveryRetryRequest,
  });
}

type RetryVariables = { deliveryIds: string[]; type?: string; metric?: string };

/** @public */
export const useRetryDeliveries = (options: BulkOpHookOptions<RetryVariables> = {}) =>
  useBulkDeliveryMutation<RetryVariables>({
    options,
    perform: (workspaceId, variables) => retryDeliveries({ workspaceId, ...variables }),
    pendingField: 'pendingRetry',
  });

/* --- redeliver (a re-send; marks pendingSend) --- */
export interface RedeliverDeliveriesInput extends BulkOpBase {
  /** Whether the resend should track opens/clicks (the Resend modal's radio). */
  tracked?: boolean;
}

/** @public */
export async function redeliverDeliveries(input: RedeliverDeliveriesInput): Promise<void> {
  await hydraUiApi.deliveries.redeliver({
    params: { path: { environment_id: String(input.workspaceId) } },
    // The generated request requires `overrides`; we send only the fields the
    // backend needs for a selected-rows resend (`tracked` is nullable/optional).
    body:
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      {
        delivery_ids: input.deliveryIds,
        ...(input.tracked == null ? {} : { tracked: input.tracked }),
      } as DeliveriesDeliveryRedeliverRequest,
  });
}

type RedeliverVariables = { deliveryIds: string[]; tracked?: boolean };

/** @public */
export const useRedeliverDeliveries = (options: BulkOpHookOptions<RedeliverVariables> = {}) =>
  useBulkDeliveryMutation<RedeliverVariables>({
    options,
    perform: (workspaceId, variables) => redeliverDeliveries({ workspaceId, ...variables }),
    pendingField: 'pendingSend',
  });

type DeliveryIdsVariables = { deliveryIds: string[] };

/* --- delete (type-safe per-id loop; see NOTE above) --- */
/** @public */
export async function deleteDeliveries(input: BulkOpBase): Promise<void> {
  await Promise.all(
    input.deliveryIds.map((id) =>
      hydraUiApi.deliveries.delete({ params: { path: { environment_id: String(input.workspaceId), id } } }),
    ),
  );
}

/** @public */
export const useDeleteDeliveries = (options: BulkOpHookOptions<DeliveryIdsVariables> = {}) =>
  useBulkDeliveryMutation<DeliveryIdsVariables>({
    options,
    perform: (workspaceId, variables) => deleteDeliveries({ workspaceId, ...variables }),
    pendingField: 'pendingDelete',
  });

/* --- export --- */
export interface ExportDeliveriesInput extends DeliveryExportInput {
  workspaceId: number;
  from?: string;
}

/** @public */
export async function exportDeliveries(input: ExportDeliveriesInput): Promise<ExportsExportCreatedResponse> {
  return hydraUiApi.exports.deliveries({
    params: { path: { environment_id: String(input.workspaceId) } },
    // `rawBody`, not `body`: the generated `ExportsDeliveriesRequest` marks every
    // field required (the Go struct has no `omitempty`), but the handler treats
    // absent filters as "no filter" and rejects blank ones (`,string` int tags),
    // so the builder's partial body is the payload that actually works.
    rawBody: JSON.stringify(buildDeliveriesExportBody(input, input.from)),
    headers: { 'Content-Type': 'application/json' },
  });
}

type ExportVariables = Omit<ExportDeliveriesInput, 'workspaceId'>;

/** @public */
export const useExportDeliveries = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ExportsExportCreatedResponse, ExportVariables>;
} = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: ExportVariables) => exportDeliveries({ workspaceId, ...variables }),
    onSuccess,
  });
};
